import { useState, useRef, useEffect } from "react"
import { motion } from "framer-motion"
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { 
    LockIcon, 
    CheckCircle2, 
    AlertCircle,
    Ban,
    ShieldAlert,
    EyeIcon,
    EyeOffIcon,
    XCircle
} from 'lucide-react'
import { useEnhancedToast } from "@/components/ui/enhanced-toast"
import { useTenant } from "@/context/TenantContext"
import { useTheme } from "@/context/ThemeContext"
import { resetPassword } from "@/api"
import { cn } from "@/lib/utils"

interface PasswordRequirement {
    regex: RegExp;
    label: string;
}

const passwordRequirements: PasswordRequirement[] = [
    { regex: /.{8,}/, label: "At least 8 characters" },
    { regex: /[A-Z]/, label: "One uppercase letter" },
    { regex: /[a-z]/, label: "One lowercase letter" },
    { regex: /[0-9]/, label: "One number" },
    { regex: /[^A-Za-z0-9]/, label: "One special character" },
];

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate()
    const { error, success } = useEnhancedToast()
    const { branding } = useTenant()
    const { theme } = useTheme()

    // Determine if we should use dark logo
    const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const effectiveLogoUrl = isDarkMode && branding?.dark_logo_url ? branding.dark_logo_url : branding?.logo_url;

    const errorRef = useRef(error);
    const successRef = useRef(success);

    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)
    const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(event.target.value)

    const validatePassword = (password: string): boolean => {
        return passwordRequirements.every(requirement => requirement.regex.test(password));
    }

    const getPasswordStrength = (password: string): number => {
        return passwordRequirements.filter(req => req.regex.test(password)).length;
    }

    async function onSubmit(event: React.FormEvent) {
        event.preventDefault()
        
        if (!token) {
            errorRef.current({
                icon: Ban,
                title: "Missing Token",
                description: "Missing reset token. Please use the link from your email.",
                duration: 4000,
            });
            return;
        }

        if (!validatePassword(password)) {
            errorRef.current({
                icon: ShieldAlert,
                title: "Invalid Password",
                description: "Please meet all password requirements",
                duration: 4000,
            });
            return;
        }

        if (password !== confirmPassword) {
            errorRef.current({
                icon: XCircle,
                title: "Passwords Don't Match",
                description: "Please ensure both passwords are identical",
                duration: 4000,
            });
            return;
        }

        setIsLoading(true)

        try {
            const result = await resetPassword({token, password})
            if (result.error) {
                errorRef.current({
                    icon: AlertCircle,
                    title: "Invalid Token",
                    description: "Invalid reset token. Please use the link from your email.",
                    duration: 4000,
                });
                return
            }

            successRef.current({
                icon: CheckCircle2,
                title: "Password Reset",
                description: result.data?.message || "Your password has been reset successfully",
                duration: 4000,
            });
            navigate("/auth/login", { replace: true })
        } finally {
            setIsLoading(false)
        }
    }

    const containerVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        }
    }

    const strengthPercentage = (getPasswordStrength(password) / passwordRequirements.length) * 100;
    const strengthColor = strengthPercentage <= 40 ? "bg-destructive" : 
                         strengthPercentage <= 80 ? "bg-yellow-500 dark:bg-yellow-600" : 
                         "bg-green-500 dark:bg-green-600";

    const renderLogo = () => {
        if (!effectiveLogoUrl) {
            return (
                <span className="text-2xl font-semibold text-primary">
                    {branding?.company_name || 'VoiceBot'}
                </span>
            );
        }

        return (
            <img
                src={effectiveLogoUrl}
                alt={branding?.company_name || 'Company Logo'}
                className="max-h-12 sm:max-h-14 w-auto max-w-[180px] sm:max-w-[220px] object-contain"
                onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    const parent = target.parentElement;
                    if (parent) {
                        parent.innerHTML = `<span class="text-2xl font-semibold text-primary">${branding?.company_name || 'VoiceBot'}</span>`;
                    }
                }}
            />
        );
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="w-full max-w-md mx-auto"
        >
            <Card className="shadow-lg border">
                <CardContent className="p-6 sm:p-8 space-y-6">
                    {/* Logo */}
                    <div className="flex flex-col items-center space-y-6">
                        <div className="flex items-center justify-center min-h-12 max-h-16 py-2">
                            {renderLogo()}
                        </div>
                        <div className="text-center space-y-1.5">
                            <h1 className="text-xl font-semibold tracking-tight">Create new password</h1>
                            <p className="text-sm text-muted-foreground/80">
                                Your new password must be different from previously used passwords.
                            </p>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={onSubmit} className="space-y-4">
                        {/* Password */}
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-sm font-medium">
                                New Password
                            </Label>
                            <div className="relative">
                                <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    id="password"
                                    value={password}
                                    onChange={handlePasswordChange}
                                    placeholder="Enter new password"
                                    type={showPassword ? "text" : "password"}
                                    className="pl-9 pr-9 h-9"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showPassword ? (
                                        <EyeOffIcon className="h-4 w-4" />
                                    ) : (
                                        <EyeIcon className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                            {/* Password strength indicator */}
                            {password && (
                                <div className="space-y-2">
                                    <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                                        <div 
                                            className={cn("h-full transition-all duration-300", strengthColor)}
                                            style={{ width: `${strengthPercentage}%` }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="text-sm font-medium">
                                Confirm Password
                            </Label>
                            <div className="relative">
                                <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    id="confirmPassword"
                                    value={confirmPassword}
                                    onChange={handleConfirmPasswordChange}
                                    placeholder="Confirm new password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    className="pl-9 pr-9 h-9"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showConfirmPassword ? (
                                        <EyeOffIcon className="h-4 w-4" />
                                    ) : (
                                        <EyeIcon className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Password requirements */}
                        {password && (
                            <div className="space-y-2 pt-1">
                                <p className="text-xs font-medium text-foreground">Password must contain:</p>
                                <div className="space-y-1.5">
                                    {passwordRequirements.map((req, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            {req.regex.test(password) ? (
                                                <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />
                                            ) : (
                                                <div className="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground/30 shrink-0" />
                                            )}
                                            <span className={cn(
                                                "text-xs transition-colors",
                                                req.regex.test(password) ? 'text-foreground font-medium' : 'text-muted-foreground'
                                            )}>
                                                {req.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="pt-2">
                            <Button
                                type="submit"
                                disabled={isLoading || !validatePassword(password) || password !== confirmPassword}
                                className="w-full h-9"
                            >
                                {isLoading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Resetting...
                                    </span>
                                ) : (
                                    'Reset Password'
                                )}
                            </Button>
                        </div>
                    </form>

                    {/* Login Link */}
                    <div className="text-center text-sm text-muted-foreground/80">
                        Remember your password?{" "}
                        <Link 
                            to="/auth/login" 
                            className="text-primary hover:text-primary/80 transition-colors font-medium"
                        >
                            Sign in
                        </Link>
                    </div>
                </CardContent>
            </Card>

            {/* Footer Links */}
            <div className="mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground/70">
                <Link 
                    to="/terms-of-service" 
                    className="hover:text-foreground/80 transition-colors"
                >
                    Terms of Service
                </Link>
                <span>•</span>
                <Link 
                    to="/privacy-policy" 
                    className="hover:text-foreground/80 transition-colors"
                >
                    Privacy Policy
                </Link>
            </div>
        </motion.div>
    )
}
